/**
 * Página de ProPoints para entrenadores
 *
 * Muestra:
 * - Lista de ProPoints asignados
 * - Formularios para crear nuevos ProPoints
 * - Historial de actas de partido
 */

import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

const CoachEvaluaciones = () => {
  const { coachData: _coachData } = useOutletContext();
  const [clasificacion, setClasificacion] = useState([]);
  const [logrosDisponibles, setLogrosDisponibles] = useState([]);
  const [filtroLogro, setFiltroLogro] = useState('');
  const [loading, setLoading] = useState(true);
  const [paginacion, setPaginacion] = useState({
    paginaActual: 1,
    totalPaginas: 1,
    totalJugadores: 0,
    jugadoresPorPagina: 10,
    hayPaginaAnterior: false,
    hayPaginaSiguiente: false
  });

  // Variable responsive
  const isMobile = window.innerWidth <= 768;

  // Scroll al top cuando se carga la página
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Cargar clasificación de ProPoints
  useEffect(() => {
    const fetchClasificacion = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('prokids_token');

        let url = `/api/coach/evaluaciones?page=${paginacion.paginaActual}&limit=${paginacion.jugadoresPorPagina}`;
        if (filtroLogro) {
          url += `&logro=${encodeURIComponent(filtroLogro)}`;
        }

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setClasificacion(result.data.clasificacion || []);
            setLogrosDisponibles(result.data.logrosDisponibles || []);
            setPaginacion(result.data.paginacion || paginacion);
          }
        } else {
          console.error('❌ Error al cargar clasificación:', response.status);
        }
      } catch (error) {
        console.error('Error al cargar clasificación:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClasificacion();
  }, [filtroLogro, paginacion.paginaActual]);

  // Funciones de paginación
  const irAPagina = (numeroPagina) => {
    setPaginacion(prev => ({
      ...prev,
      paginaActual: numeroPagina
    }));
  };

  const paginaAnterior = () => {
    if (paginacion.hayPaginaAnterior) {
      irAPagina(paginacion.paginaActual - 1);
    }
  };

  const paginaSiguiente = () => {
    if (paginacion.hayPaginaSiguiente) {
      irAPagina(paginacion.paginaActual + 1);
    }
  };

  // Función para cambiar filtro (resetea a página 1)
  const cambiarFiltro = (nuevoFiltro) => {
    setFiltroLogro(nuevoFiltro);
    setPaginacion(prev => ({
      ...prev,
      paginaActual: 1
    }));
  };

  // Función para obtener emoji del logro
  const getLogroEmoji = (logro) => {
    switch (logro) {
      case 'asistencia': return '👥';
      case 'victoria': return '🏆';
      case 'destacado': return '⭐';
      case 'El Jugón': return '🎮';
      case 'El Cerrojo': return '🛡️';
      case 'El Desequilibrante': return '⚡';
      case 'El Líder': return '👑';
      case 'El Mejor Compañero': return '🤝';
      default: return '🏅';
    }
  };

  // Función para obtener estilo del badge
  const getBadgeStyle = (logro) => {
    const baseStyle = {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      margin: '2px',
      color: 'white'
    };

    switch (logro) {
      case 'asistencia':
        return { ...baseStyle, backgroundColor: '#17a2b8' };
      case 'victoria':
        return { ...baseStyle, backgroundColor: '#28a745' };
      case 'destacado':
        return { ...baseStyle, backgroundColor: '#ffc107', color: '#212529' };
      case 'El Jugón':
        return { ...baseStyle, backgroundColor: '#dc3545' };
      case 'El Cerrojo':
        return { ...baseStyle, backgroundColor: '#6f42c1' };
      case 'El Desequilibrante':
        return { ...baseStyle, backgroundColor: '#fd7e14' };
      case 'El Líder':
        return { ...baseStyle, backgroundColor: '#20c997' };
      case 'El Mejor Compañero':
        return { ...baseStyle, backgroundColor: '#e83e8c' };
      default:
        return { ...baseStyle, backgroundColor: '#6c757d' };
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <div style={{ textAlign: 'center', color: '#4352A9' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⭐</div>
          <p>Cargando ProPoints...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '20px 16px' : '40px 24px',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      
      {/* Header */}
      <div style={{
        marginBottom: isMobile ? '24px' : '32px'
      }}>
        <h1 style={{
          fontSize: isMobile ? '1.8rem' : '2.5rem',
          fontWeight: '700',
          color: '#061156',
          margin: '0 0 8px 0'
        }}>
          🏆 Clasificación ProPoints
        </h1>
        <p style={{
          fontSize: isMobile ? '14px' : '16px',
          color: '#6c757d',
          margin: 0
        }}>
          Ranking de jugadores por ProPoints obtenidos
        </p>
      </div>

      {/* Filtros */}
      {logrosDisponibles.length > 0 && (
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: isMobile ? '20px' : '24px',
          marginBottom: isMobile ? '20px' : '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e1e5e9'
        }}>
          <h3 style={{
            fontSize: isMobile ? '1.1rem' : '1.2rem',
            fontWeight: '600',
            color: '#061156',
            margin: '0 0 16px 0'
          }}>
            🔍 Filtrar por Logro
          </h3>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center'
          }}>
            <button
              onClick={() => cambiarFiltro('')}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: filtroLogro === '' ? '2px solid #4352A9' : '1px solid #dee2e6',
                backgroundColor: filtroLogro === '' ? '#4352A9' : '#ffffff',
                color: filtroLogro === '' ? '#ffffff' : '#6c757d',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              📊 Todos los Logros
            </button>

            {logrosDisponibles.map(logro => (
              <button
                key={logro.nombre}
                onClick={() => cambiarFiltro(logro.nombre)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: filtroLogro === logro.nombre ? '2px solid #4352A9' : '1px solid #dee2e6',
                  backgroundColor: filtroLogro === logro.nombre ? '#4352A9' : '#ffffff',
                  color: filtroLogro === logro.nombre ? '#ffffff' : '#6c757d',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {getLogroEmoji(logro.nombre)} {logro.nombre} (+{logro.puntos})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Contenido principal */}
      {clasificacion.length === 0 ? (
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: isMobile ? '40px 20px' : '60px 40px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e1e5e9'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📊</div>
          <h3 style={{
            fontSize: isMobile ? '1.2rem' : '1.4rem',
            fontWeight: '600',
            color: '#061156',
            margin: '0 0 8px 0'
          }}>
            {filtroLogro ? `No hay datos para "${filtroLogro}"` : 'No hay ProPoints'}
          </h3>
          <p style={{
            fontSize: isMobile ? '14px' : '15px',
            color: '#6c757d',
            margin: '0 0 24px 0'
          }}>
            {filtroLogro
              ? `Ningún jugador ha obtenido el logro "${filtroLogro}" aún`
              : 'Aún no has asignado ProPoints a ningún jugador'
            }
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: isMobile ? '12px' : '16px'
        }}>
          {clasificacion.map((jugador) => (
            <div
              key={jugador.futbolista_id}
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: isMobile ? '16px' : '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e1e5e9',
                position: 'relative'
              }}
            >
              {/* Posición en el ranking */}
              <div style={{
                position: 'absolute',
                top: '16px',
                left: '16px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: jugador.posicion <= 3 ? '#ffd700' : '#4352A9',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                {jugador.posicion}
              </div>

              <div style={{
                marginLeft: '48px'
              }}>
                {/* Información del jugador */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div>
                    <h3 style={{
                      fontSize: isMobile ? '1.1rem' : '1.2rem',
                      fontWeight: '600',
                      color: '#061156',
                      margin: '0 0 4px 0'
                    }}>
                      {jugador.futbolista_nombre}
                      {jugador.futbolista_apodo && (
                        <span style={{
                          color: '#6c757d',
                          fontWeight: '400',
                          fontSize: '0.9em',
                          marginLeft: '8px'
                        }}>
                          &ldquo;{jugador.futbolista_apodo}&rdquo;
                        </span>
                      )}
                    </h3>
                  </div>

                  <div style={{
                    backgroundColor: '#4352A9',
                    color: '#ffffff',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    {jugador.puntos_totales} pts
                  </div>
                </div>

                {/* Badges de logros */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px'
                }}>
                  {jugador.logros.map((logro, logroIndex) => (
                    <span
                      key={logroIndex}
                      style={getBadgeStyle(logro.logro_nombre)}
                      title={`${logro.logro_nombre}: ${logro.cantidad} veces (+${logro.puntos_totales} pts)`}
                    >
                      {getLogroEmoji(logro.logro_nombre)} {logro.logro_nombre} x{logro.cantidad} (+{logro.puntos_totales})
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Controles de paginación */}
      {clasificacion.length > 0 && paginacion.totalPaginas > 1 && (
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: isMobile ? '20px' : '24px',
          marginTop: isMobile ? '20px' : '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e1e5e9'
        }}>
          {/* Información de paginación */}
          <div style={{
            textAlign: 'center',
            marginBottom: '16px',
            fontSize: '14px',
            color: '#6c757d'
          }}>
            Mostrando {((paginacion.paginaActual - 1) * paginacion.jugadoresPorPagina) + 1} - {Math.min(paginacion.paginaActual * paginacion.jugadoresPorPagina, paginacion.totalJugadores)} de {paginacion.totalJugadores} jugadores
          </div>

          {/* Botones de navegación */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            {/* Botón anterior */}
            <button
              onClick={paginaAnterior}
              disabled={!paginacion.hayPaginaAnterior}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                backgroundColor: paginacion.hayPaginaAnterior ? '#ffffff' : '#f8f9fa',
                color: paginacion.hayPaginaAnterior ? '#4352A9' : '#6c757d',
                fontSize: '14px',
                fontWeight: '500',
                cursor: paginacion.hayPaginaAnterior ? 'pointer' : 'not-allowed',
                transition: 'all 0.3s ease'
              }}
            >
              ← Anterior
            </button>

            {/* Números de página */}
            {Array.from({ length: paginacion.totalPaginas }, (_, i) => i + 1).map(numeroPagina => (
              <button
                key={numeroPagina}
                onClick={() => irAPagina(numeroPagina)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: paginacion.paginaActual === numeroPagina ? '2px solid #4352A9' : '1px solid #dee2e6',
                  backgroundColor: paginacion.paginaActual === numeroPagina ? '#4352A9' : '#ffffff',
                  color: paginacion.paginaActual === numeroPagina ? '#ffffff' : '#4352A9',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '36px'
                }}
              >
                {numeroPagina}
              </button>
            ))}

            {/* Botón siguiente */}
            <button
              onClick={paginaSiguiente}
              disabled={!paginacion.hayPaginaSiguiente}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                backgroundColor: paginacion.hayPaginaSiguiente ? '#ffffff' : '#f8f9fa',
                color: paginacion.hayPaginaSiguiente ? '#4352A9' : '#6c757d',
                fontSize: '14px',
                fontWeight: '500',
                cursor: paginacion.hayPaginaSiguiente ? 'pointer' : 'not-allowed',
                transition: 'all 0.3s ease'
              }}
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default CoachEvaluaciones;
